import {
  Checkbox,
  Radio,
  Spacing,
  Spinner,
  Typography,
  theme as defaultTheme,
} from "@mindlercare/ui-web";
import { useId } from "react";
import { styled } from "styled-components";
import type {
  AnswerOption,
  QuestionnaireContent,
  QuestionnaireQuestion,
  UserAnswer,
} from "../types";
import { useIcbtTranslation } from "../translations";
import { mergeContentWithAnswers } from "./utils";

export { mergeContentWithAnswers };

export type QuestionnaireVariant = "answers" | "full";

export type QuestionnaireProps = {
  content?: QuestionnaireContent;
  answers?: UserAnswer[];
  requestId?: string;
  isLoading?: boolean;
  /**
   * "answers" renders only the selected option(s) as a compact summary.
   * "full" renders every option as a (disabled) radio/checkbox list.
   */
  variant?: QuestionnaireVariant;
};

export const Questionnaire = ({
  content,
  answers = [],
  requestId,
  isLoading,
  variant = "full",
}: QuestionnaireProps) => {
  const questionnaire = mergeContentWithAnswers(requestId, content, answers);

  if (isLoading) return <Spinner />;

  return (
    <QuestionnaireContentContainer $variant={variant}>
      {questionnaire?.questions.map((question, i) => (
        <QuestionnaireItem
          key={question.questionId}
          data={question}
          questionIndex={i + 1}
          variant={variant}
        />
      ))}
    </QuestionnaireContentContainer>
  );
};

const QuestionnaireItem = ({
  data,
  questionIndex,
  variant,
}: {
  data: QuestionnaireQuestion & { userAnswers: AnswerOption[] };
  questionIndex: number;
  variant: QuestionnaireVariant;
}) => {
  const uniqueId = useId();
  const groupName = `${data.questionId}-${uniqueId}`;

  return (
    <div>
      <Typography variant={variant === "answers" ? "body-bold" : "h3"}>
        {questionIndex}. {data.name ?? "--"}
      </Typography>
      <Spacing b={8} />
      {variant === "answers" ? (
        <SelectedAnswers userAnswers={data.userAnswers} />
      ) : (
        <QuestionOptions>
          {data.optionGroup.options.map((option) => (
            <QuestionOption key={option.optionId}>
              {data.type === "SELECT_ONE" ? (
                <Radio
                  name={groupName}
                  value={option.optionId}
                  checked={
                    data.userAnswers.find(
                      ({ optionId }) => optionId === option.optionId,
                    ) !== undefined
                  }
                  label={option.name}
                  disabled
                />
              ) : (
                <Checkbox
                  name={groupName}
                  value={option.optionId}
                  checked={
                    data.userAnswers.find(
                      ({ optionId }) => optionId === option.optionId,
                    ) !== undefined
                  }
                  label={option.name}
                  disabled
                />
              )}
            </QuestionOption>
          ))}
        </QuestionOptions>
      )}
    </div>
  );
};

const SelectedAnswers = ({ userAnswers }: { userAnswers: AnswerOption[] }) => {
  const t = useIcbtTranslation();

  if (userAnswers.length === 0) {
    return (
      <Typography variant="body-small" color="GRAY">
        {t.NO_ANSWER}
      </Typography>
    );
  }

  return (
    <SelectedAnswersList>
      {userAnswers.map((answer) => (
        <SelectedAnswer key={answer.optionId}>
          <Typography variant="body-small">{answer.name}</Typography>
        </SelectedAnswer>
      ))}
    </SelectedAnswersList>
  );
};

const QuestionnaireContentContainer = styled.div<{
  $variant: QuestionnaireVariant;
}>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$variant === "answers" ? defaultTheme.SPACING.MD : "0")};
`;

const SelectedAnswersList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${defaultTheme.SPACING.XS};
`;

const SelectedAnswer = styled.div`
  width: fit-content;
  background-color: ${defaultTheme.COLORS.GRAY_LIGHT};
  padding: 2px 8px;
  border-radius: 12px;
`;

const QuestionOptions = styled.div`
  background-color: ${defaultTheme.COLORS.GRAY_LIGHT};
  padding: ${defaultTheme.SPACING.XS};

  & > div {
    border-bottom: 1px solid ${defaultTheme.COLORS.GRAY_MEDIUM};
  }

  & > div:last-child {
    border-bottom: none;
  }
`;

const QuestionOption = styled.div`
  display: flex;
  align-items: center;
  padding: ${defaultTheme.PADDING.SM};
`;
